import React, { FC, useEffect, useMemo, useState } from "react"

import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  BellOutlined,
  ContactsOutlined,
  ExperimentOutlined,
  FileOutlined,
  FundOutlined,
  FundViewOutlined,
  HomeOutlined,
  ReadOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  UserOutlined
} from "@ant-design/icons"
import { Badge, ConfigProvider, Flex, Layout, Menu, Modal } from "antd"
import { Link, NavLink, useLocation } from "react-router-dom"

import variables from "@/_styles/variables.module.scss"
import { SvgImage } from "@/components/_Shared/SvgImage/SvgImage"
import { CustomerSwapper } from "@/components/Customers/CustomerSwapper"
import { Resource, Scope } from "@/constants/permission"
import { useNotificationContext } from "@/context/NotificationContext"
import { useTimeCoordinateContext } from "@/context/TimeCoordinateContext"

import classes from "./Sidebar.module.scss"

import { useMenuLinks } from "@/hook/MenuLinks/useMenuLinksQuery"
import { usePermission } from "@/hook/usePermission"

import { MenuOption, MenuPermission } from "@/types/ui"

import { paths } from "@/router/paths"


const menuItems: any[] = [
  getItem(
    <NavLink to={paths.account}>Главная страница</NavLink>,
    paths.account,
    {
      permission: { resource: Resource.Account, scope: Scope.Menu },
      icon: <HomeOutlined/>
    }
  ),
  getItem(
    <NavLink to={paths.ordersHistory}>Заказы</NavLink>,
    paths.ordersHistory,
    {
      permission: { resource: Resource.CustomerOrderHistory, scope: Scope.Menu },
      icon: <ShoppingCartOutlined/>
    }
  ),
  getItem(
    <NavLink to={paths.managerAccount}>Главная страница</NavLink>,
    paths.managerAccount,
    {
      permission: { resource: Resource.ManagerAccount, scope: Scope.Menu },
      icon: <HomeOutlined/>
    }
  ),
  getItem("Топливо", "fuels-menu", { icon: <ExperimentOutlined /> }, [
    getItem(
      <NavLink to={paths.fuelTypes}>Тип топлива</NavLink>,
      paths.fuelTypes,
      {
        permission: { resource: Resource.FuelType, scope: Scope.Menu }
      }
    ),
    getItem(<NavLink to={paths.fuels}>Цены на топливо</NavLink>, paths.fuels, {
      permission: { resource: Resource.Fuel, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.fuelLimits}>Лимиты на топливо</NavLink>, paths.fuelLimits, {
      permission: { resource: Resource.FuelLimit, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.fuelArchive}>Архив цен на топливо</NavLink>, paths.fuelArchive, {
      permission: { resource: Resource.FuelArchive, scope: Scope.Menu }
    })
  ]),
  getItem(<NavLink to={paths.orders}>Заказы</NavLink>, paths.orders, {
    permission: { resource: Resource.Order, scope: Scope.Menu },
    icon: <ShoppingCartOutlined/>
  }),
  getItem(
    <NavLink to={paths.dispatcherOrders}>Заказы</NavLink>,
    paths.dispatcherOrders,
    {
      permission: { resource: Resource.DispatcherOrders, scope: Scope.Menu },
      icon: <ShoppingCartOutlined/>
    }
  ),
  getItem(
    <NavLink to={paths.dispatcherTimeCoordinate}>Согласование времени</NavLink>,
    paths.dispatcherTimeCoordinate,
    {
      permission: { resource: Resource.DispatcherTimeCoordinate, scope: Scope.Menu },
      icon: <ShoppingCartOutlined/>
    }
  ),
  getItem("Финансы", "finance-section", { icon: <FundOutlined /> }, [
    getItem(<NavLink to={paths.finance}>Финансы</NavLink>, paths.finance, {
      permission: { resource: Resource.Finance, scope: Scope.Menu }
    }),
    getItem(
      <NavLink to={paths.customerFinance}>Финансы клиента</NavLink>, paths.customerFinance, {
      permission: { resource: Resource.CustomerFinance, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.financeReport}>Финансовый отчёт</NavLink>, paths.financeReport, {
      permission: { resource: Resource.FinanceReport, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.financeReturnFunds}>Возвраты средств</NavLink>, paths.financeReturnFunds, {
      permission: { resource: Resource.FinanceReturnFunds, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.financeData}>Финансовые данные из 1С</NavLink>, paths.financeData, {
      permission: { resource: Resource.FinanceData, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.financeDataFromMailbox}>Финансовые данные из банка</NavLink>, paths.financeDataFromMailbox, {
      permission: { resource: Resource.FinanceDataFromMailbox, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.financeDocuments}>Документы</NavLink>, paths.financeDocuments, {
      permission: { resource: Resource.FinanceDocuments, scope: Scope.Menu }
    })
  ]),
  getItem("Справочники", "catalogs", { icon: <ReadOutlined /> }, [
    getItem(<NavLink to={paths.drivers}>Водители</NavLink>, paths.drivers, {
      permission: { resource: Resource.Driver, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.transport}>Транспорт</NavLink>, paths.transport, {
      permission: { resource: Resource.Transport, scope: Scope.Menu }
    }),
    getItem(
      <NavLink to={paths.customerCategories}>Категории клиентов</NavLink>,
      paths.customerCategories,
      {
        permission: { resource: Resource.CustomerCategory, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.customerClients}>Клиенты</NavLink>,
      paths.customerClients,
      {
        permission: { resource: Resource.CustomerClient, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.customers}>Контрагенты</NavLink>,
      paths.customers,
      {
        permission: { resource: Resource.Customer, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.depots}>Нефтебазы</NavLink>,
      paths.depots,
      {
        permission: { resource: Resource.Depot, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.deliveryAddresses}>Адреса доставки</NavLink>,
      paths.deliveryAddresses,
      {
        permission: { resource: Resource.DeliveryAddress, scope: Scope.Menu }
      }
    ),
    getItem(<NavLink to={paths.qualityPassports}>Паспорта качества</NavLink>, paths.qualityPassports, {
      permission: { resource: Resource.QualityPassports, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.timeUnloadFuels}>Слоты времени отгрузки</NavLink>, paths.timeUnloadFuels, {
      permission: { resource: Resource.Fuel, scope: Scope.Menu }
    }),
    getItem(
      <NavLink to={paths.years}>Буквенный год</NavLink>,
      paths.years,
      {
        permission: { resource: Resource.Year, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.carriers}>Перевозчики</NavLink>,
      paths.carriers,
      {
        permission: { resource: Resource.Carrier, scope: Scope.Menu }
      }
    )
  ]),
  getItem("Отчеты", "reports", { icon: <FundViewOutlined /> }, [
    getItem(
      <NavLink to={paths.orderReports}>Заказы</NavLink>,
      paths.orderReports,
      {
        permission: { resource: Resource.OrderReport, scope: Scope.Menu }
      }
    )
  ]),
  getItem(<NavLink to={paths.users}>Пользователи</NavLink>, paths.users, {
    permission: { resource: Resource.User, scope: Scope.Menu },
    icon: <UserOutlined/>
  }),
  getItem(<NavLink to={paths.roles}>Роли</NavLink>, paths.roles, {
    permission: { resource: Resource.Role, scope: Scope.Menu },
    icon: <ContactsOutlined/>
  }),
  getItem(<NavLink to={paths.notificationSettings}>Настройка уведомлений</NavLink>, paths.notificationSettings, {
    permission: { resource: Resource.NotificationSettings, scope: Scope.Menu },
    icon: <SettingOutlined/>
  }),
  getItem(<NavLink to={paths.menuLinksPage}>Ссылки в меню</NavLink>, paths.menuLinksPage, {
    permission: { resource: Resource.MenuLinks, scope: Scope.Menu },
    icon: <SettingOutlined/>
  }),
  getItem(<NavLink to={paths.notifications}>Уведомления</NavLink>, paths.notifications,
    {
      permission: { resource: Resource.Notification, scope: Scope.Menu },
      icon: <BellOutlined/>
    }
  ),
  getItem("Аналитика", "analytics", { icon: <BarChartOutlined /> }, [
    getItem(<NavLink to={paths.pricesAnalytics}>Динамика цен</NavLink>, paths.pricesAnalytics, {
      permission: { resource: Resource.PricesAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.ordersAnalytics}>Динамика заказов</NavLink>, paths.ordersAnalytics, {
      permission: { resource: Resource.OrdersAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.refusalsAnalytics}>Динамика отказов</NavLink>, paths.refusalsAnalytics, {
      permission:
        { resource: Resource.RefusalsAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.offersAnalytics}>Динамика предложений</NavLink>, paths.offersAnalytics, {
      permission:
        { resource: Resource.OffersAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.salesAnalytics}>Динамика продаж</NavLink>, paths.salesAnalytics, {
      permission: { resource: Resource.SalesAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.limitsAnalytics}>Динамика открытий фондов</NavLink>, paths.limitsAnalytics, {
      permission: { resource: Resource.LimitsAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.limitsSalesAnalytics}>Динамика продаж-открытий</NavLink>, paths.limitsSalesAnalytics, {
      permission: { resource: Resource.LimitsSalesAnalytics, scope: Scope.Menu }
    }),
    getItem(<NavLink to={paths.deliveriesPickupsAnalytics}>Динамика доставок/самовывоза</NavLink>,
      paths.deliveriesPickupsAnalytics, {
      permission: { resource: Resource.DeliveriesPickupsAnalytics, scope: Scope.Menu }
    })
  ]),
  getItem("Доверенности", "authorities-section", { icon: <FileOutlined /> }, [
    getItem(
      <NavLink to={paths.authorities}>Доверенности</NavLink>,
      paths.authorities,
      {
        permission: { resource: Resource.Authorities, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.authorityLogs}>Логи обновления</NavLink>,
      paths.authorityLogs,
      {
        permission: { resource: Resource.AuthoritiesLogs, scope: Scope.Menu }
      }
    ),
    getItem(
      <NavLink to={paths.authorityRegisterLogs}>Логи регистрации</NavLink>,
      paths.authorityRegisterLogs,
      {
        permission: { resource: Resource.AuthoritiesLogs, scope: Scope.Menu }
      }
    )
  ]),
  getItem(
    <NavLink to={paths.customerSettings}>Настройки</NavLink>,
    paths.customerSettings,
    {
      permission: { resource: Resource.CustomerSettings, scope: Scope.Menu },
      icon: <SettingOutlined />
    }
  )
]

const openKeysMap: Record<string, string[]> = {
  "fuel-types": ["fuels-menu", "fuel-types"],
  "fuel-archive": ["fuels-menu", "fuel-archive"],
  account: ["client-personal", "account"],
  "order-history": ["client-personal", "order-history"],
  "manager-account": ["manager-account"],
  orders: ["orders"],
  "order-reports": ["reports", "order-reports"],
  "customer-clients": ["catalogs", "customer-clients"],
  fuels: ["fuels-menu", "fuels"],
  "fuel-limits": ["fuels-menu", "fuel-limits"],
  customers: ["catalogs", "customers"],
  "customer-categories": ["catalogs", "customer-categories"],
  drivers: ["catalogs", "drivers"],
  transports: ["catalogs", "transports"],
  users: ["users"],
  roles: ["roles"],
  "addresses": ["catalogs", "delivery-addresses"],
  "analytics-prices": ["analytics", "analytics-prices"],
  "analytics-orders": ["analytics", "analytics-orders"],
  "analytics-refusals": ["analytics", "analytics-refusals"],
  "analytics-offers": ["analytics", "analytics-offers"],
  "analytics-sells": ["analytics", "analytics-sells"],
  "analytics-limits": ["analytics", "analytics-limits"],
  "analytics-limits-sells": ["analytics", "analytics-limits-sells"],
  "analytics-deliveries-pickups": ["analytics", "analytics-deliveries-pickups"],
  "customer-settings": ["customer-settings"],
  "authorities": ["authorities-section", "authorities"],
  "authorities-logs": ["authorities-section", "authorities-logs"],
  "authorities-register-logs": ["authorities-section", "authorities-register-logs"],
  finance: ["finance-section", "finance"],
  customerFinance: ["finance-section", "customer-finance"],
  "finance-report": ["finance-section", "customer-report"],
  "finance-data": ["finance-section", "customer-data"],
  "finance-returns": ["finance-section", "customer-returns"]
}


export const Sidebar: FC = React.memo(() => {
  const location = useLocation()
  const [keyPath, setKeyPath] = useState<string>(
    location.pathname.split("/")[1] || ""
  )
  const { hasPermission } = usePermission()

  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [shownLogo, setShownLogo] = useState<boolean>(true)
  const [currentCustomerName, setCurrentCustomerName] = useState<string>("")

  const { countNotReadNotifications, countAwaitsVerification } = useNotificationContext()
  const { countTimeCoordinateOrders } = useTimeCoordinateContext()

  const { data: menuLinks } = useMenuLinks()
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState<boolean>()

  const toggleCollapsed = (isCollapsed: boolean) => {
    setCollapsed(isCollapsed)
    setShownLogo(!isCollapsed)
  }

  const badgeStyle = {
    color: "#FF3737",
    overflowCount: 100
  }
  const noBorder = { "border": "none !important" }

  useEffect(() => {
    if (location.pathname) {
      const key = location.pathname.split("/")[1]
      if (key) setKeyPath(key)
    }
  }, [location.pathname])

  const generateCatalogMenuItems = (key: string, children: any[]) => {
    var lableText = ""
    var countUpdates = 0

    switch (key) {
      case paths.transport:
        lableText = "Транспорт"
        countUpdates = countAwaitsVerification.countTranspoert
        break
      case paths.drivers:
        lableText = "Водители"
        countUpdates = countAwaitsVerification.countDriver
        break
      case paths.deliveryAddresses:
        lableText = "Адреса доставки"
        countUpdates = countAwaitsVerification.countDeliveryAddress
        break
    }


    return {
      label:
        <NavLink to={key} className={classes["navLink-flex"]}>
          {lableText}
          {!collapsed && countUpdates > 0 &&
            <Badge size="small" count={countUpdates} style={{ ...noBorder, "marginRight" : "1px" }} {...badgeStyle} />
          }
        </NavLink>,
      key: key.includes("/") ? key.split("/")[1] : key,
      children: children
    }
  }

  const menus = useMemo(() => {
    const generateMenuItems = (menuItems: any): any => {
      if (!menuItems) return
      const items = []

      for (const menu of menuItems) {
        if (
          menu.permission &&
          !hasPermission(menu.permission.resource, menu.permission.scope)
        )
          continue

        const key: string = menu.key
        const children = generateMenuItems(menu.children) as any[]
        if (children && !children.some((x) => x)) {
          continue
        }

        if(key == "customerName-current") {
          items.push({
            label: currentCustomerName,
            key: "customerName-current",
            icon: <UserOutlined />
          })
          continue
        }

        if (key === paths.drivers || key === paths.transport || key === paths.deliveryAddresses) {
          items.push(generateCatalogMenuItems(key, children))
        }

        else if (key === paths.dispatcherTimeCoordinate) {
          items.push({
            label:
              <NavLink to={paths.dispatcherTimeCoordinate} className={classes["navLink-flex"]}>
                Согласование времени
                {!collapsed && countTimeCoordinateOrders> 0 &&
                  <Badge size="small" count={countTimeCoordinateOrders} style={{ ...noBorder, "marginRight" : "1px" }} {...badgeStyle} />
                }
              </NavLink>,
            key:  key.includes("/") ? key.split("/")[1] : key,
            icon: collapsed ? (
              <Badge size="small" count={countTimeCoordinateOrders} style={{ ...noBorder, "marginTop" : "10px" }} {...badgeStyle}>
                <BellOutlined />
              </Badge>
            ) : <BellOutlined />,
            children: children
          })
        } else if (key === paths.notifications) {
          items.push({
            label:
              <NavLink to={paths.notifications} className={classes["navLink-flex"]}>
                Уведомления
                {!collapsed && 
                  <Badge size="small"  count={countNotReadNotifications}  overflowCount={100} style={{ "marginRight" : "1px" }} />
                }
              </NavLink>,
            key: key.includes("/") ? key.split("/")[1] : key,
            icon: collapsed ? (
              <Badge size="small" count={countNotReadNotifications} style={{ ...noBorder, "marginTop" : "10px" }} {...badgeStyle}>
                <BellOutlined />
              </Badge>
              ) : <BellOutlined />,
            children: children
          })
        }
        else {
          items.push({
            label: menu.label,
            key: key.includes("/") ? key.split("/")[1] : key,
            icon: menu.icon,
            children: children
          })
        }

        items.push({
          type: "divider"
        })
      }

      return items.slice(0, -1)
    }

    return generateMenuItems(menuItems)
  }, [hasPermission, countNotReadNotifications, countAwaitsVerification, countTimeCoordinateOrders, collapsed, currentCustomerName])

  return (
    <Layout.Sider
      breakpoint="lg"
      trigger={null}
      width={300}
      className={classes.sidebar}
      collapsible
      collapsed={collapsed}
      onCollapse={toggleCollapsed}
      collapsedWidth={60}
      onBreakpoint={(broken) => {
        setShownLogo(!broken)
      }}
    >
      <div className={classes["sidebar-main"]}>
        <Flex
          justify="space-between"
          align="center"
          className={classes.logowrapper}
        >
          {collapsed ?
            <ArrowRightOutlined onClick={() => toggleCollapsed(!collapsed)}/>
            : <ArrowLeftOutlined onClick={() => toggleCollapsed(!collapsed)}/>
          }

          {hasPermission(Resource.Account, Scope.Menu) &&
            <CustomerSwapper
              collapsed={collapsed}
              setCurrentCustomerName={setCurrentCustomerName}
              currentCustomerName={currentCustomerName}
            />
          }
        </Flex>

        <ConfigProvider
          theme={{
            hashed: false,
            components: {
              Menu: {
                itemHeight: "55px",
                itemBg: "transparent",
                darkItemBg: "transparent",
                itemSelectedBg: variables.baseBgColor,
                darkItemSelectedBg: variables.baseBgColor,
                itemSelectedColor: variables.textMenuSelected,
                darkItemSelectedColor: variables.textMenuSelected,
                subMenuItemBg: "transparent",
                darkSubMenuItemBg: "transparent",
                groupTitleFontSize: 16
              }
            }
          }}
        >
          <Menu
            className={classes.menu}
            theme="dark"
            mode="inline"
            defaultOpenKeys={openKeysMap[keyPath]}
            selectedKeys={[keyPath]}
            items={menus}
          >
          </Menu>
        </ConfigProvider>
        <div className={classes["transparent_gradient"]}></div>
      </div>
      {
        !collapsed && hasPermission(Resource.MenuLinks, Scope.Read) &&
          <Flex className={classes["menu-links"]}>
            {
              menuLinks?.response.isWhatsappVisible &&
                <Link
                    target="_blank"
                    to={`https://api.whatsapp.com/send?phone=${menuLinks?.response.whatsappNumber}`}
                    className={classes["menu-links-whatsapp"]}>
                    <SvgImage width={33} height={33} src={"/social-networks/whatsapp-icon-2.svg"}/>
                    <span>Обратная связь</span>
                    <div style={{ width: "33px" }}>
                    </div>
                </Link>
            }
            {
              menuLinks?.response.isTelegramVisible &&
                <Link
                    target="_blank"
                    to={`https://t.me/${menuLinks?.response.telegramId}`}
                    className={classes["menu-links-telegram"]}
                >
                    <Flex className={classes["menu-links-telegram-content"]}>
                        <img src="/social-networks/logo-bear.jpg" alt="" className={classes["menu-links-telegram-content-logo"]}/>
                    </Flex>
                    <span>Обратная связь</span>
                    <div style={{ width: "33px" }}>
                    </div>
                </Link>
            }
            <Flex gap={"small"} style={{ flexDirection: "column" }}>
                <span className={classes["company-name"]}>НОРД</span>
                <span className={classes["all-rights-reserved"]}>© 2024 All right reserved</span>
                <span
                    className={classes["privacy-policy"]}
                    onClick={() => setIsPrivacyPolicyModalOpen(true)}>
                    Политика конфиденциальности
                </span>
            </Flex>
          </Flex>
      }
      <Modal
        width={"45%"}
        open={isPrivacyPolicyModalOpen}
        title="Политика конфиденциальности"
        onOk={() => setIsPrivacyPolicyModalOpen(false)}
        onCancel={() => setIsPrivacyPolicyModalOpen(false)}
        footer={[]}
      >
        {
          menuLinks?.response.privacyPolicy
        }
      </Modal>
    </Layout.Sider>
  )
})

function getItem(
  label: React.ReactNode,
  key: React.Key,
  options?: MenuOption,
  children?: MenuPermission[]
): MenuPermission {
  return {
    key,
    children,
    label,
    icon: options?.icon,
    permission: options?.permission
  } as MenuPermission
}